/* Imports needed for all question form subclasses. */
import * as React from 'react';
import { compose } from 'lodash/fp';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router';
import {
    mapStateToQuestionFormBaseProps,
    mapDispatchToQuestionFormBaseProps
} from '../../QuestionFormBase';
import {
    QuestionFormWithOptionsBase,
    QuestionFormWithOptionsBaseProps,
    QuestionFormWithOptionsBaseStates
} from '../QuestionFormWithOptionsBase';
import { MULTIMEDIA_TYPES, OPTION_TYPES, QTYPES } from '@nexxt/common/types';
import { connect } from 'react-redux';
import NotifyHOC from '../../../../../../HOC/Notifications';

/* Components used in this question form. */
import BuilderHeader from '../../../common/builderHeader';
import QuestionSettings from '../../../common/questionSettings';
import MediaLibraryAddModal from '../../../../../libraries/mediaLibrary/MediaLibraryAddModal';
import UploadVideo from '../../../common/uploadMedia/v2/UploadVideo';
import { DEFAULT_TEXT } from '../../../../../../constants/defaultText';
import { LOCK_PROJECT } from '../../../../../../constants/project';
import { alertMessage } from '../../../../../../constants/alertMessage';
import MultimediaBubble from '../../../common/MultimediaBubble';
import HelperTooltip from '../../../../../common/HelperTooltip';
import { DEFAULT_TRANSLATION_BUTTONS } from '../../../../../../constants/defaultOption';
import CheckboxMultiSelect from '../../../../../common/reactSelect/CheckboxMultiSelect';
import CollaspeButton from '../../../common/optionCard/CollaspeButton';

/* Imports specifically for vector map question form. */

interface Props extends QuestionFormWithOptionsBaseProps {
    // nothing special... yet!
    t?: (text: string, options?: Record<string, boolean>) => string; // translate function
}

interface States extends QuestionFormWithOptionsBaseStates {
    // nothing special... yet!
}

class VideoAnnotation extends QuestionFormWithOptionsBase<Props, States> {
    /* Constructor just needs to specify this QTYPE! */
    constructor(p: QuestionFormWithOptionsBaseProps) {
        super(p, QTYPES.VIDEO_ANNOTATION);
        const { isNewQuestion } = this.props;
        console.log(
            DEFAULT_TRANSLATION_BUTTONS(
                QTYPES.VIDEO_ANNOTATION,
                this.props.t,
                this.props.currentProject.languages.map((l) => l.value)
            )
        );
        const question = {
            ...this.state.question,
            title: isNewQuestion
                ? DEFAULT_TEXT.QUESTION.VIDEO_ANNOTATION.TITLE
                : this.state.question.title,
            showContinueButton: true,
            playVideo: true,
            min: isNewQuestion ? 3 : this.state.question.min,
            buttons: isNewQuestion
                ? DEFAULT_TRANSLATION_BUTTONS(
                      QTYPES.VIDEO_ANNOTATION,
                      this.props.t,
                      this.props.currentProject.languages.map((l) => l.value)
                  )
                : this.state.question.buttons
        };
        this.state = {
            ...this.state,
            showCardSettings: true,
            question
        };
    }

    /* Here's where all the magic happens. */
    render() {
        const { currentProject, editEnabled } = this.props;
        const {
            isSettingBoxLayoutOpen,
            isMediaLibraryOpen,
            question,
            isOptionExpand,
            templateSettings
        } = this.state;
        const questionError =
            (this.state.showError && this.state.questionError) || {};

        const unlockSaveAndMedia = !editEnabled
            ? !!templateSettings || false
            : true;
        let lockMessage;
        if (templateSettings?.alertMessages?.length) {
            lockMessage = templateSettings?.alertMessages;
        } else {
            lockMessage = this.getWarningMessage();
        }
        return (
            <div>
                <div className="project-add--question-builder">
                    {isMediaLibraryOpen && (
                        <MediaLibraryAddModal
                            isOpen={this.state.isMediaLibraryOpen}
                            setSelectedMedia={this.selectMedia}
                            notify={this.props.notify}
                            onCancel={this.closeMediaLibrary}
                            onClose={this.closeMediaLibrary}
                            type={this.state.mediaType}
                            showMediaLibrary={true}
                            videoOnly={templateSettings?.videoOnly}
                        />
                    )}
                    <BuilderHeader
                        toggleSettingBoxLayout={this.toggleSettingBoxLayout}
                        isSettingBoxLayoutOpen={isSettingBoxLayoutOpen}
                        showAlert={!this.props.editEnabled}
                        alertMessages={lockMessage}
                        question={question}
                        setValue={this.setQuestion}
                        disabled={!this.props.editEnabled}
                        errorObject={this.state.questionError}
                    />
                    {/** Beginning of builder body */}
                    <div className="builder-body">
                        {isSettingBoxLayoutOpen && (
                            <QuestionSettings
                                onClose={this.toggleSettingBoxLayout}
                                question={question}
                                questionSettings={{
                                    allowUserEdit: true
                                }}
                                setValue={this.setQuestion}
                                disabled={!this.props.editEnabled}
                            />
                        )}
                        {this.renderErrorNotes()}
                        <MultimediaBubble
                            question={question}
                            languages={currentProject.languages}
                            setValue={this.setQuestion}
                            setValueWithLanguage={
                                this.NEW_setQuestionTextTranslation
                            }
                            addNewBubble={this.NEW_addNewEditor}
                            removeBubble={this.NEW_removeEditor}
                            disabled={!this.props.editEnabled}
                            questionError={questionError}
                            settings={{
                                isHTMLText: true,
                                showClose: true
                            }}
                            defaultText={
                                DEFAULT_TEXT.QUESTION.VIDEO_ANNOTATION
                                    .QUESTION_TEXT
                            }
                        />
                        <UploadVideo
                            videoURL={question.videoURL}
                            label="Upload Video"
                            appyCurrentImage={({ mediaURL }) =>
                                this.setQuestion('videoURL', mediaURL)
                            }
                            position="full"
                            disabled={!unlockSaveAndMedia}
                        />
                        <div className="option-settings-block">
                            <div className="form-group-header display-flex">
                                Options
                            </div>

                            <div className="row">
                                <label className="col-md-12 form-group-header">
                                    Option types
                                </label>
                                <div className="col-md-12 form-group">
                                    <CheckboxMultiSelect
                                        options={DEFAULT_TRANSLATION_BUTTONS(
                                            this.state.question.type,
                                            this.props.t,
                                            this.props.currentProject.languages.map(
                                                (l) => l.value
                                            )
                                        )}
                                        value={question.buttons || []}
                                        onChangeCallback={(response) =>
                                            this.setQuestion(
                                                'buttons',
                                                response
                                            )
                                        }
                                    />
                                </div>
                            </div>
                            <div className="form-group-header display-flex">
                                Options
                                <CollaspeButton
                                    isOptionExpand={isOptionExpand}
                                    toggleOptionExpand={this.toggleOptionExpand}
                                />
                            </div>

                            {(questionError &&
                                (questionError.buttons || {}).isError && (
                                    <div
                                        className={`form-group ${
                                            questionError ? 'error' : ''
                                        }`}
                                    >
                                        <label className="form-group__label form-group__label--block">
                                            {questionError.buttons
                                                ?.emptyButtons ||
                                                alertMessage.BUTTON_ERROR}
                                        </label>
                                    </div>
                                )) ||
                                null}
                            {/** Show Option Cards according to option type */}
                            {isOptionExpand &&
                                this.renderOptionCard(
                                    OPTION_TYPES.IMAGE_AND_TEXT,
                                    {
                                        disableLabel: false,
                                        disableValue: true,
                                        disableRemove: true,
                                        disableDrag: true,
                                        disableImage: true
                                    },
                                    'buttons'
                                )}
                        </div>
                        <div className="optional-blocks">
                            <div className="form-group-header">
                                <HelperTooltip
                                    pathname="Min Number of Questions"
                                    placement="bottom"
                                    textSize="small"
                                >
                                    Min # of comments
                                </HelperTooltip>
                            </div>
                            <div
                                className={`form-group ${
                                    questionError.min ? 'error' : ''
                                }`}
                            >
                                <input
                                    type="number"
                                    className="form-group__control"
                                    onChange={(e) =>
                                        this.setQuestion('min', e.target.value)
                                    }
                                    min={0}
                                    disabled={!editEnabled}
                                    value={question.min}
                                />
                                {questionError.min ? (
                                    <span className="form-group__error-msg">
                                        {questionError.min}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                        <MultimediaBubble
                            languages={currentProject.languages}
                            question={question}
                            setValue={this.setQuestion}
                            setValueWithLanguage={
                                this.NEW_setQuestionTextTranslation
                            }
                            questionError={questionError}
                            keyName="continueTexts"
                            disabled={!this.props.editEnabled}
                            disableAddBubble
                            defaultTextType={MULTIMEDIA_TYPES.TEXT}
                            questionText="Continue button text"
                            defaultText="BUTTON.CONTINUE"
                            settings={{ noBorder: true, useTranslation: true }}
                        />
                    </div>

                    {/** End of builder body */}
                </div>
            </div>
        );
    }
}

/* Component export wrapped in redux connector, wrapped in notify HOC */
export default NotifyHOC(
    compose(
        withTranslation(),
        connect(
            mapStateToQuestionFormBaseProps,
            mapDispatchToQuestionFormBaseProps
        ),
        withRouter
    )(VideoAnnotation)
);
